import { createSelector } from 'reselect';
import { type CoveragePremiums, type Premium } from 'dto/CoveragePremium';
import { CoverageProduct, type Coverage } from 'dto/marketableProduct';
import { useAppStore, type AppStore } from 'state/store';
import { filterAvailableCoverages } from 'state/utils/coverage-init';
import { getInitialState, PremiumPeriod } from 'utils';
import {
  calculatePremiumTotal,
  computeSelectedPremiums,
  getSelectablePremiums,
} from 'utils/premium-utils';

/**
 * Base selectors: Direct property access from store
 */
const selectCoveragePremiums = (state: AppStore) => state.coveragePremiums;
const selectCoverageSelections = (state: AppStore) => state.coverageSelections;
const selectSelectedCoverages = (state: AppStore) => state.selectedCoverages;
const selectBackgroundInfoForm = (state: AppStore) => state.backgroundInfoForm;

/**
 * Memoized selector: Get Premium objects for selected coverages
 * Derives Premium objects from coveragePremiums and coverageSelections
 */
export const selectSelectedPremiums = createSelector(
  [selectCoveragePremiums, selectCoverageSelections],
  (coveragePremiums, coverageSelections): CoveragePremiums => {
    return computeSelectedPremiums(coveragePremiums, coverageSelections);
  }
);

/**
 * Hook: Get Premium objects for selected coverages
 */
export const useSelectedPremiums = (): CoveragePremiums => {
  return useAppStore(selectSelectedPremiums);
};

/**
 * Parameterized selector: Get selectable premiums for a specific coverage product
 * Note: This is a hook selector that takes a parameter. It uses .use hooks internally
 * to access store state, which is appropriate for parameterized selectors.
 */
export const useSelectablePremiums = (
  coverageProduct: CoverageProduct
): Premium[] => {
  const coveragePremiums = useAppStore.use.coveragePremiums();
  const selectedCoverages = useAppStore.use.selectedCoverages();

  if (!coveragePremiums) {
    return [];
  }

  const coveragePremium = coveragePremiums.find(
    (cp: { coverageProduct: CoverageProduct }) =>
      cp.coverageProduct === coverageProduct
  );

  if (!coveragePremium) {
    return [];
  }

  // Use existing utility for LIFE product filtering
  if (coverageProduct === CoverageProduct.LIFE) {
    return getSelectablePremiums(selectedCoverages, coveragePremium);
  }

  // For other products, return all premiums
  return coveragePremium.premiums;
};

/**
 * Memoized selector: Get available coverages based on age and working status
 * Replaces the useMemo in AppProvider
 */
export const selectAvailableCoverages = createSelector(
  [selectBackgroundInfoForm],
  (backgroundInfoForm): Coverage[] | undefined => {
    const { marketableProduct } = getInitialState();
    return filterAvailableCoverages(
      marketableProduct?.coverages,
      backgroundInfoForm
    );
  }
);

/**
 * Hook: Get available coverages based on age and working status
 */
export const useAvailableCoverages = (): Coverage[] | undefined => {
  return useAppStore(selectAvailableCoverages);
};

/**
 * Memoized selector: Calculate monthly premium total
 */
export const selectMonthlyTotal = createSelector(
  [selectSelectedPremiums, selectSelectedCoverages],
  (selectedPremiums, selectedCoverages): number => {
    return calculatePremiumTotal(
      selectedPremiums,
      selectedCoverages,
      PremiumPeriod.MONTHLY
    );
  }
);

/**
 * Hook: Calculate monthly premium total
 */
export const useMonthlyTotal = (): number => {
  return useAppStore(selectMonthlyTotal);
};

/**
 * Memoized selector: Calculate annual premium total
 */
export const selectAnnualTotal = createSelector(
  [selectSelectedPremiums, selectSelectedCoverages],
  (selectedPremiums, selectedCoverages): number => {
    return calculatePremiumTotal(
      selectedPremiums,
      selectedCoverages,
      PremiumPeriod.ANNUAL
    );
  }
);

/**
 * Hook: Calculate annual premium total
 */
export const useAnnualTotal = (): number => {
  return useAppStore(selectAnnualTotal);
};

/**
 * Selector: Detect if life insurance fallback premium was assigned
 * Returns true when life insurance sum insured was adjusted upward to meet minSumInsured threshold
 */
export const useLifeInsuranceFallbackAssigned = (): boolean => {
  return useAppStore.use.lifeInsuranceFallbackPremiumAssigned();
};

/**
 * Memoized selector: Get all cart totals and premiums in a single subscription
 */
export const selectCartSummary = createSelector(
  [selectSelectedPremiums, selectMonthlyTotal, selectAnnualTotal],
  (selectedPremiums, monthlyTotal, annualTotal) => {
    return {
      selectedPremiums,
      monthlyTotal,
      annualTotal,
    };
  }
);

/**
 * Hook: Get cart summary (premiums and totals) in a single subscription
 */
export const useCartSummary = () => {
  return useAppStore(selectCartSummary);
};
